import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Activity, Loader2, Bell, Radar, Target, Megaphone, BadgeCheck, Waypoints, Trophy, Send, ListChecks } from "lucide-react";

const Stat = ({ label, value, accent, testid }) => (
  <div className="flex flex-col" data-testid={testid}>
    <span className="text-[9px] uppercase tracking-widest text-atlas-secondary/85 font-mono">{label}</span>
    <span className={`text-base font-mono leading-tight ${accent || "text-atlas-cyan"}`}>{value}</span>
  </div>
);

const NAV = "flex items-center gap-1.5 px-3 text-[10px] font-mono uppercase tracking-wider text-white/85 hover:bg-white/10 transition-colors";

const Badge = ({ n, testid }) => (
  <span data-testid={testid}
    className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-atlas-red text-white text-[9px] font-mono atlas-pulse">
    {n}
  </span>
);

const fmtCountdown = (ms) => {
  if (ms == null) return "live";
  if (ms <= 0) return "any moment";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
};

export const TelemetryHeader = ({ telemetry, incidentCount, alertCount = 0, foresightCount = 0, onOpenAlerts, onOpenCalibration, onOpenBroadcasts, onOpenForesight }) => {
  const t = telemetry || {};
  const sources = t.sources || {};
  const scanning = !!t.scanning;

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const nextMs = t.next_scan_at ? new Date(t.next_scan_at).getTime() - now : null;
  const statusLabel = scanning
    ? "Scanning feeds…"
    : t.next_scan_at
    ? `Next scan in ${fmtCountdown(nextMs)}`
    : "Autonomous · live";

  const journeyDay = t.journey_started_at
    ? Math.floor((now - new Date(t.journey_started_at).getTime()) / 86400000) + 1
    : null;

  return (
    <header
      className="h-14 flex items-center px-5 gap-5 border-b border-white/10 shrink-0 bg-atlas-surface"
      data-testid="telemetry-header"
    >
      {/* INFORMATION — plain text, no borders */}
      <div className="flex items-center gap-5 min-w-0 flex-1" data-testid="header-info">
        <img src="/vigilo-wordmark-dark.png" alt="Vigilo Earth — The Earth is always signalling." className="h-9 w-auto object-contain shrink-0" data-testid="atlas-logo" />

        <div className="hidden md:flex flex-col leading-tight shrink-0" data-testid="autonomous-status"
          title="Vigilo Earth runs fully autonomously — no manual ingest or triage needed">
          <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-atlas-secondary">
            {scanning ? <Loader2 className="w-3 h-3 text-atlas-orange animate-spin" /> : <Activity className="w-3 h-3 text-atlas-cyan atlas-pulse" />}
            {scanning ? "Scanning" : "Autonomous · streaming"}
          </span>
          <span className="text-[10px] font-mono text-atlas-secondary/85" data-testid="autonomous-status-label">
            {statusLabel}
            {journeyDay && (
              <span data-testid="journey-badge" className="text-atlas-cyan/80"
                title={`Vigilo Earth's live record started ${new Date(t.journey_started_at).toUTCString()} — nothing before this counts as an Vigilo Earth detection`}>
                {" "}· Day {journeyDay}
              </span>
            )}
          </span>
        </div>

        <div className="hidden xl:flex items-center gap-2 shrink-0" data-testid="source-chips">
          {["NASA", "ESA", "NOAA", "USGS"].map((k) => {
            const on = (sources[k] ?? 0) > 0;
            return (
              <span key={k} data-testid={`source-${k.toLowerCase()}`} title={on ? `${k} feed online` : `${k} — no data this cycle`}
                className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-atlas-secondary">
                <span className={`w-1.5 h-1.5 rounded-full ${on ? "bg-emerald-400" : "bg-white/20"}`} />
                {k}
              </span>
            );
          })}
        </div>

        <div className="hidden lg:flex items-center gap-5 shrink-0"
          title="Incidents: distinct real-world situations Vigilo Earth is flagging right now (many readings grouped into one)">
          <Stat label="Anomaly" value={incidentCount ?? t.unexplained_anomalies ?? 0} accent="text-atlas-red" testid="stat-anomalies" />
        </div>
      </div>

      {/* ACTIONS — distinct filled bar behind a divider */}
      <div className="flex items-center gap-4 pl-5 border-l border-white/15 h-full shrink-0">
        <nav className="hidden sm:flex items-stretch h-9 bg-white/[0.06] border border-white/15 divide-x divide-white/10" data-testid="header-nav">
          <Link to="/confirmed" data-testid="confirmed-btn" className={`${NAV} hover:text-emerald-300`}
            title="Confirmed Events — before-vs-now satellite pairs for verified Vigilo Earth calls">
            <BadgeCheck className="w-3.5 h-3.5 text-emerald-400" /> Confirmed
          </Link>
          <Link to="/dam-record" data-testid="dam-record-btn" className={`${NAV} hover:text-sky-300`}
            title="Dam Detection Record — historic landslide dams replayed through the live radar detector">
            <Waypoints className="w-3.5 h-3.5 text-sky-400" /> Dam Record
          </Link>
          <Link to="/leaderboard" data-testid="leaderboard-btn" className={`${NAV} hover:text-amber-200`}
            title="Lead-Time Leaderboard — hazards and countries ranked by how early Vigilo Earth spots them">
            <Trophy className="w-3.5 h-3.5 text-amber-300" /> Leaderboard
          </Link>
          <Link to="/scoring" data-testid="scoring-btn" className={`${NAV} hover:text-lime-200`}
            title="Public Scoring Ledger — every prediction with its outcome, evidence, basin and lead time (CSV export)">
            <ListChecks className="w-3.5 h-3.5 text-lime-300" /> Scoring
          </Link>
          <Link to="/outbox" data-testid="outbox-btn" className={`${NAV} hover:text-atlas-cyan`}
            title="Social Outbox — approve confirmed share cards for X and LinkedIn">
            <Send className="w-3.5 h-3.5 text-atlas-cyan" /> Outbox
          </Link>
          <button onClick={onOpenForesight} data-testid="foresight-btn" className={`${NAV} relative hover:text-atlas-red`}
            title="Event Foresight — cross-layer precursor synthesis to flag risk before the event">
            <Radar className="w-3.5 h-3.5 text-atlas-red" /> Foresight
            {foresightCount > 0 && <Badge testid="foresight-badge" n={foresightCount} />}
          </button>
          <button onClick={onOpenCalibration} data-testid="calibration-btn" className={`${NAV} hover:text-atlas-cyan`}
            title="Calibration & backtested track record — how often Vigilo Earth is actually right">
            <Target className="w-3.5 h-3.5 text-atlas-cyan" /> Track Record
          </button>
          <button onClick={onOpenBroadcasts} data-testid="broadcasts-btn" className={`${NAV} hover:text-atlas-cyan`}
            title="Vigilo Earth broadcasts — auto-posted breakthrough images & captions">
            <Megaphone className="w-3.5 h-3.5 text-atlas-cyan" /> Broadcasts
          </button>
          <button onClick={onOpenAlerts} data-testid="alerts-bell-btn" className={`${NAV} relative px-2.5 hover:text-atlas-red`}
            title="Early-warning broadcast">
            <Bell className="w-3.5 h-3.5" />
            {alertCount > 0 && <Badge testid="alerts-badge" n={alertCount} />}
          </button>
        </nav>
      </div>
    </header>
  );
};
